<script setup>

import {computed, defineComponent, ref, provide, onMounted, h, watch} from "vue";
import axios from "@/plugins/axios";
import {XCircleIcon} from "@heroicons/vue/24/outline";
const props = defineProps({
  id: {
    type: [Number, String],
    default: 0
  },
  name: {
    type: String,
    default: ''
  },
  closable: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: Object,
    default: null,
  },
  childLinks: {
    type: Array,
    default: () => [],
  },
  contentClass: {
    type: [String, Array, Object],
    default: '',
  }
})

const page = ref(props.modelValue || {});

const model = computed(() => {

  return page.value.template ?? {
    id: 0,
    tag: 'div',
    option: {},
    children: []
  }
})
const rootClass = computed(() => page.value.template?.option?.class || []);
const rootStyle = computed(() => page.value.template?.option?.style || {});

function moveLocation() {
  if(page.value.link){
    window.open(page.value.link, '_blank');
  }
}
provide('$editor', {});
const fetch = async () => {
  if (props.modelValue) {
    page.value = props.modelValue;
    return;
  }
  if (!props.name) return;
  const {data} = await axios.get('/api/cms/published/' + props.name)
  page.value = data;
}
onMounted(() => {
  fetch();
})

watch(() => props.modelValue, (value) => {
  page.value = value || {};
}, {deep: true});

const visible = ref(true)

function hide() {
  visible.value = false;
}

function show() {
  visible.value = true;
}

const isVisible = computed(() => {
  if (!props.closable) return visible.value;
  if( typeof localStorage === 'undefined') return false;
  // 로컬스토리지에서 캐싱이 유효한지 확인한다.
  const date = localStorage.getItem('cms-page-' + props.name);
  if (date) {
    const now = new Date();
    if (now.getTime() < date) {
      return false;
    }else{
      // 캐싱이 유효하지 않으면 캐싱을 삭제한다.
      localStorage.removeItem('cms-page-' + props.name);
    }
  }
  return visible.value;
})
// LocalStorage 에 캐싱하고 오늘 24시 전까지는 보이지 않도록 한다.
function setCaching()
{
  // 로컬 스토리지가 없다면 실행 중지
  if( typeof localStorage === 'undefined') return;

  const date = new Date();
  date.setHours(24,0,0,0);
  localStorage.setItem('cms-page-' + props.name, date.getTime());
}

function renderer(child){
  const sourceTag = child.tag ?? 'div';
  const tag = {
    'layout-component': 'div',
    'text-component': 'div',
    'image-component': 'img',
  }[sourceTag] || sourceTag;
  const componentProps = child.option?.props || {};
  const isImage = sourceTag === 'image-component' || tag === 'img';

  const children = child.children
    ?.map((nestedChild) => renderer(nestedChild))
    .filter(Boolean);

  return h(tag, {
    id: child.id,
    class: child.option?.class,
    style: {
      ...(child.option?.style || {}),
      ...(isImage ? {
        objectFit: componentProps['object-fit'],
        width: componentProps.width,
        height: componentProps.height,
      } : {}),
    },
    ...(isImage ? {
      src: componentProps.src,
      alt: componentProps.alt || page.value.name || 'CW 서비스 이미지',
    } : {
      innerHTML: componentProps.innerText,
    }),
  }, children)
}

const CmsNodeRenderer = defineComponent({
  name: 'CmsNodeRenderer',
  props: {
    node: {
      type: Object,
      required: true,
    },
  },
  setup(rendererProps) {
    return () => renderer(rendererProps.node);
  },
});

defineExpose({
  model,
  hide,
  show
})
</script>

<template>
  <div
    v-if="closable"
    v-show="isVisible"
    class="relative"
    :class="[rootClass, contentClass]"
    :style="rootStyle"
  >
    <template
      v-for="child in page?.template?.children"
      :key="child.id"
    >
      <cms-node-renderer
        :node="child"
        class="cursor-pointer"
        @click="moveLocation"
      />
    </template>
    <div
      class="absolute right-0 top-0 flex items-center h-full p-4 cursor-pointer"
      @click="()=>{
        visible = false;
        setCaching();
      }"
    >
      <XCircleIcon class="w-8 text-white" />
    </div>
  </div>
  <div
    v-else
    v-show="isVisible"
    :class="[rootClass, contentClass]"
    :style="rootStyle"
  >
    <template
      v-for="(child, index) in page?.template?.children"
      :key="child.id"
    >
      <router-link
        v-if="childLinks[index]"
        :to="childLinks[index]"
        :title="`${page.name || 'CW 서비스'} ${index + 1}`"
        class="block min-w-0"
      >
        <cms-node-renderer :node="child" />
      </router-link>
      <cms-node-renderer v-else :node="child" />
    </template>
  </div>
</template>

<style scoped>

</style>
